import { ErrorBoundary, type JSX } from 'solid-js';
import { A } from '@solidjs/router';

// Route-level safety net. Wraps every route's children so a render
// failure in one component doesn't black-hole the rest of the app —
// the user sees a recovery card with a "Try again" button (which the
// solid-js ErrorBoundary's `reset()` callback re-mounts the children)
// plus a path back to the homepage.
//
// Solid's ErrorBoundary catches synchronous render errors AND errors
// thrown inside resource fetchers / effects that the boundary catches.
// Combined with the route-level <Suspense> in app.tsx, every route
// gets both an in-flight loading state AND a crash-recovery state.
//
// We deliberately don't try to surface the error message to the user —
// it might leak internals. The console.error call retains it for the
// dev tools / error-tracking integration.

export default function RouteErrorBoundary(props: {
  children: JSX.Element;
}): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(err, reset) => {
        // eslint-disable-next-line no-console
        console.error('Route render error:', err);
        return (
          <section class="px-6 py-16 max-w-md mx-auto text-center">
            <h1 class="font-display text-2xl font-bold">Something went wrong</h1>
            <p class="mt-3 text-sm text-brand-green/80">
              This page hit an error and couldn't render. The rest of the
              app is fine — you can retry, or head back to the homepage.
            </p>
            <div class="mt-5 flex gap-2 justify-center flex-wrap">
              <button
                type="button"
                onClick={reset}
                class="rounded-full bg-brand-green text-brand-cream px-5 py-2 text-sm font-medium cursor-pointer hover:bg-brand-moss transition-colors"
              >
                Try again
              </button>
              <A
                href="/"
                class="rounded-full border border-brand-hex text-brand-green px-5 py-2 text-sm font-medium hover:bg-brand-hex/40 transition-colors"
              >
                Back to homepage
              </A>
            </div>
          </section>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
}
